import {useTheme} from '@mui/material';
import {useIsMutating} from '@tanstack/react-query';
import {useCallback, useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {MUTATION_KEYS} from 'infra/libs/react-query/mutationKeys';
import {useSubscriptionDialogsStateStore} from 'presentation/components/SubscriptionDialogs/store/useSubscriptionDialogsStateStore';
import {FeaturesEnum, HomeEnum} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {
	useAccountInformation,
	useChangeDataProfile,
	useMediaQueries,
} from 'presentation/pages/private/hooks';

import {translations} from 'presentation/translations-new';
import {getPageHeaderTitle} from 'presentation/utils';
import {PATH_TITLE} from '../../booking/constants';

import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useProfileMenuItems} from '../hooks/useProfileMenuItems';
import {useProfileSectionStore} from '../store/useProfileSection';

export function useProfilePage() {
	const navigate = useNavigate();
	const {isLaptop, isTablet, isMobile} = useMediaQueries();
	const {state: routeState} = useLocation();
	const {isGndiSaoPaulo} = useHostName();
	const theme = useTheme();

	const {isEnabled: isNewProfileFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.ADD__PORTAL_FEATURE_NEW_PROFILE,
	);

	const isMutatingEmailToken = useIsMutating({mutationKey: [MUTATION_KEYS.CHANGE_EMAIL]}) > 0;

	const isMutatingPhoneToken = useIsMutating({mutationKey: [MUTATION_KEYS.CHANGE_PHONE]}) > 0;

	const changeDataProfile = useChangeDataProfile();
	const accountInfo = useAccountInformation();

	const {
		REGISTRATION_DATA_ITEMS,
		LOGIN_AND_PRIVACY_ITEMS,
		showB2BToggle,
		isAlreadyRegisteredModalOpen,
		setIsAlreadyRegisteredModalOpen,
		isFavoriteAddressesFetched,
	} = useProfileMenuItems();

	const showBankData =
		accountInfo.accesses.refund &&
		accountInfo.isHolder &&
		!accountInfo.odontoOnly &&
		isGndiSaoPaulo;

	const {title} = translations['pt-br'].privateFlow.profile;

	const pageTitle = getPageHeaderTitle({
		internalPaths: PATH_TITLE,
		mainPath: title,
	});

	const setDialogs = useSubscriptionDialogsStateStore(state => state.updateState);
	const dialogs = useSubscriptionDialogsStateStore(state => state.state);

	const activeSection = useProfileSectionStore(state => state.activeSection);
	const setActiveSection = useProfileSectionStore(state => state.setActiveSection);

	if (dialogs.isSubscriptionModalOpen) {
		setDialogs({isSubscriptionModalOpen: false});
	}

	const handleBack = useCallback(() => {
		if (routeState) {
			navigate(routeState);
			return;
		}
		navigate(HomeEnum.HOME_PAGE);
	}, [routeState]);

	useEffect(() => {
		changeDataProfile.resetChangeDataFormState();
	}, []);

	return {
		theme,
		isLaptop,
		isTablet,
		isMobile,
		pageTitle,
		handleBack,
		isNewProfileFeatureFlagEnabled,
		isMutatingEmailToken,
		isMutatingPhoneToken,
		changeDataProfile,
		accountInfo,
		REGISTRATION_DATA_ITEMS,
		LOGIN_AND_PRIVACY_ITEMS,
		showB2BToggle,
		showBankData,
		activeSection,
		setActiveSection,
		isAlreadyRegisteredModalOpen,
		setIsAlreadyRegisteredModalOpen,
		isFavoriteAddressesFetched,
	};
}
